import React from 'react';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Twitter } from 'lucide-react';
import { companyInfo } from '../data/mock';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    servicios: [
      { label: 'Aseo Industrial', href: '#services' },
      { label: 'Transporte de Carga', href: '#services' },
      { label: 'Obras Civiles', href: '#services' },
      { label: 'Proyectos Informáticos', href: '#services' }
    ],
    empresa: [
      { label: 'Sobre Nosotros', href: '#about' },
      { label: 'Nuestros Servicios', href: '#services' },
      { label: 'Contacto', href: '#contact' }
    ]
  };

  const socialLinks = [
    { icon: Facebook, href: '#', label: 'Facebook' },
    { icon: Instagram, href: '#', label: 'Instagram' },
    { icon: Linkedin, href: '#', label: 'LinkedIn' },
    { icon: Twitter, href: '#', label: 'Twitter' }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId.replace('#', ''));
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#01A9DB] rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#F58020] rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10">
        {/* Main Footer Content */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Company Info */}
            <div className="lg:col-span-1">
              <div className="mb-6">
                <h3 className="text-2xl font-bold bg-gradient-to-r from-[#01A9DB] to-[#F58020] bg-clip-text text-transparent mb-2">
                  BRAMADOR
                </h3>
                <p className="text-sm text-slate-400 font-medium">
                  Servicios Profesionales
                </p>
              </div>
              <p className="text-slate-400 leading-relaxed mb-6">
                Soluciones confiables y profesionales para empresas públicas, privadas y personas naturales en la región.
              </p>
              
              {/* Social Links */}
              <div className="flex space-x-3">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className="w-10 h-10 bg-slate-800 hover:bg-[#01A9DB] rounded-lg flex items-center justify-center transition-all duration-300 hover:scale-110 hover:-translate-y-1 group"
                  >
                    <social.icon className="w-5 h-5 text-slate-400 group-hover:text-white transition-colors" />
                  </a>
                ))}
              </div>
            </div>

            {/* Services Links */}
            <div>
              <h4 className="text-white font-bold text-lg mb-4">
                Servicios
              </h4>
              <ul className="space-y-3">
                {footerLinks.servicios.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-slate-400 hover:text-[#F58020] transition-colors duration-200 hover:translate-x-1 inline-block"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company Links */}
            <div>
              <h4 className="text-white font-bold text-lg mb-4">
                Empresa
              </h4>
              <ul className="space-y-3">
                {footerLinks.empresa.map((link, index) => (
                  <li key={index}>
                    <button
                      onClick={() => scrollToSection(link.href)}
                      className="text-slate-400 hover:text-[#F58020] transition-colors duration-200 hover:translate-x-1 inline-block"
                    >
                      {link.label}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-bold text-lg mb-4">
                Contacto
              </h4>
              <ul className="space-y-4">
                <li>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="flex items-start space-x-3 text-slate-400 hover:text-[#01A9DB] transition-colors group"
                  >
                    <Phone className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                    <span>{companyInfo.phone}</span>
                  </a>
                </li>
                <li>
                  <a
                    href={`mailto:${companyInfo.email}`}
                    className="flex items-start space-x-3 text-slate-400 hover:text-[#F58020] transition-colors group"
                  >
                    <Mail className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                    <span className="break-all">{companyInfo.email}</span>
                  </a>
                </li>
                <li>
                  <div className="flex items-start space-x-3 text-slate-400">
                    <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>Región de Chile</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-800 mb-8"></div>

          {/* Bottom Bar */}
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="text-slate-400 text-sm text-center md:text-left">
              <p>
                © {currentYear} <span className="text-[#01A9DB] font-semibold">Bramador</span>. 
                Todos los derechos reservados.
              </p>
              <p className="mt-1">
                Comprometidos con la calidad y el profesionalismo desde 2020.
              </p>
            </div>

            <div className="flex space-x-6 text-sm">
              <button className="text-slate-400 hover:text-[#01A9DB] transition-colors">
                Política de Privacidad
              </button>
              <button className="text-slate-400 hover:text-[#01A9DB] transition-colors">
                Términos de Servicio
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Accent Line */}
        <div className="h-1 bg-gradient-to-r from-[#01A9DB] via-cyan-500 to-[#F58020]"></div>
      </div>
    </footer>
  );
};
